import type { NewsletterProtectionDecision } from "./newsletterProtection";
import { normalizeDefaultDesign } from "../../../packages/core/src/newsletterDesign";
import { normalizeNewsletterTextAlignment, type NewsletterTextAlignment, sanitizeNewsletterHtml, newsletterHtmlToText, normalizeNewsletterImageHeight } from "../../../packages/core/src/newsletterText";
import type { ApiError } from "@easy-core";
import {
  NEWSLETTER_COMPOSE_STORAGE_KEY,
  type NewsletterComposeAssistantDraft,
  type NewsletterComposeDraft,
  type NewsletterDefaultDesignSnapshot,
} from "@easy-types";

import { renderAssistantEmailHtml } from "./AssistantEmailTemplate";

export type TabId = "drafts" | "sent" | "settings";

export type NewsletterSettings = {
  smtp_host: string;
  smtp_port: string;
  smtp_security: string;
  smtp_username: string;
  smtp_password: string;
  imap_host: string;
  imap_port: string;
  imap_ssl: string;
  imap_username: string;
  imap_password: string;
  imap_folder: string;
  sender_name: string;
  sender_email: string;
  reply_to_email: string;
  auth_method: string;
  oauth_provider: string;
  oauth_connected_email: string;
  oauth_connected_at: string;
  oauth_connection_mode: string;
  email_provider_selection: string;
  email_account_kind: string;
  email_onboarding_previous_sending_route: string;
  email_onboarding_previous_send_size_range: string;
  email_onboarding_previous_send_frequency: string;
  email_onboarding_intended_send_size_range: string;
  email_onboarding_intended_send_frequency: string;
  email_onboarding_intended_email_type: string;
  email_onboarding_recommended_route: string;
  email_onboarding_chosen_route: string;
  email_onboarding_initial_guardrail_profile: string;
  email_onboarding_declined_ses_recommendation_at: string;
  email_route_type: string;
  ses_identity_mode: string;
  ses_domain_identity: string;
  ses_mail_from_domain: string;
  ses_region: string;
  ses_status: string;
  ses_dns_records_json: string;
  ses_dkim_verified: string;
  ses_mail_from_verified: string;
  ses_last_checked_at: string;
  ses_last_error: string;
  ses_setup_guidance: string;
  ses_setup_warning: string;
  signature_html: string;
  public_base_url: string;
  default_batch_size: string;
  default_batch_delay_seconds: string;
};

export type EmailProviderPresetId = "custom" | "infomaniak" | "gmail" | "outlook" | "yahoo" | "icloud" | "aol";
export type EmailProviderChoiceId = EmailProviderPresetId | "unknown";
export type EmailAccountKind = "personal" | "professional" | "unknown";

type EmailProviderPreset = {
  id: Exclude<EmailProviderPresetId, "custom">;
  label: string;
  description: string;
  icon_url: string;
  brand_color: string;
  smtp_host: string;
  smtp_port: string;
  smtp_security: string;
  imap_host: string;
  imap_port: string;
  imap_ssl: string;
  note?: string;
  smtp_host_aliases?: string[];
  imap_host_aliases?: string[];
};

export type NewsletterMeta = {
  app_version: string;
  release_channel: string;
  schema_version: number;
};

export type NewsletterCampaign = {
  id: number;
  name: string;
  subject: string;
  scheduled_at: string;
  raw_recipients: string;
  contact_group_ids?: number[];
  contact_group_labels?: string[];
  html_body: string;
  tracking_enabled: boolean | number;
  unsubscribe_enabled: boolean | number;
  status: string;
  updated_at?: string;
  total_recipient_count?: number;
  sent_count?: number;
  estimated_open_count?: number;
  unique_open_count?: number | null;
  pixel_load_count?: number | null;
  tracking_method?: "shared_pixel" | "disabled";
  protection?: NewsletterProtectionDecision;
  total_open_count?: number;
  account_mode?: string;
  sending_route_profile?: string;
  provider_label?: string;
  copy_variant?: string;
  reason_codes?: string[];
  campaign_risk_level?: string;
  tracking_state?: string;
  campaign_delivery_state?: string;
  delivery_progress_percent?: number;
  remaining_recipient_count?: number;
  deferred_recipient_count?: number;
  next_batch_at?: string;
  recommended_content_simplification?: {
    level: string;
    message: string;
    points: string[];
  };
  recommended_next_action?: {
    key: string;
    label: string;
    message: string;
  };
  allowed_actions?: Record<string, string>;
  delivery_health_snapshot?: {
    bounce_rate?: number;
    smtp_failure_count?: number;
    imap_bounce_count?: number;
    recent_open_rate?: number | null;
    rolling_open_rate_median?: number | null;
    open_rate_drop_ratio?: number;
    consecutive_healthy_campaigns?: number;
    recovery_campaign_count?: number;
  };
  guardrail_summary_title?: string;
  guardrail_summary_message?: string;
  guardrail_summary_points?: string[];
};

export type CampaignAddress = {
  email: string;
  status?: string;
  status_detail?: string;
};

export type CampaignRecipient = {
  email: string;
  status: string;
  status_detail?: string;
  opened_count?: number;
};

export type NewsletterCampaignDetail = NewsletterCampaign & {
  bounced_count?: number;
  unsubscribed_count?: number;
  suppressed_count?: number;
  bad_addresses: CampaignAddress[];
  unsubscribed_addresses: CampaignAddress[];
  suppressed_addresses: CampaignAddress[];
  recent_recipients: CampaignRecipient[];
};

export type NewsletterGuardrailEvaluation = {
  protection?: NewsletterProtectionDecision;
  account_mode: string;
  campaign_risk_level?: string;
  sending_route_profile: string;
  provider_label: string;
  copy_variant: string;
  reason_codes: string[];
  tracking_state: string;
  batch_size: number;
  batch_delay_seconds: number;
  batch_delay_jitter_ratio: number;
  max_per_10_minutes?: number;
  max_per_hour?: number;
  max_per_day: number;
  max_per_30_days?: number;
  max_campaign_now: number;
  max_recipients_now?: number;
  provider_limit_snapshot: {
    max_per_hour?: number;
    max_per_day: number;
    max_per_30_days?: number;
  };
  relyo_safe_limit_snapshot: {
    max_per_hour?: number;
    max_per_day: number;
    max_per_30_days?: number;
    max_campaign_now: number;
  };
  send_now_recipient_count: number;
  send_now_remaining_count?: number;
  deferred_recipient_count: number;
  current_window_recipient_cap?: number;
  next_send_window_at?: string | null;
  campaign_delivery_state?: string;
  delivery_progress_percent?: number;
  sent_recipient_count?: number;
  remaining_recipient_count?: number;
  recommended_content_simplification?: {
    level: string;
    message: string;
    points: string[];
  };
  recommended_next_action?: {
    key: string;
    label: string;
    message: string;
  };
  allowed_actions?: Record<string, string>;
  delivery_health_snapshot?: {
    bounce_rate?: number;
    smtp_failure_count?: number;
    imap_bounce_count?: number;
    recent_open_rate?: number | null;
    rolling_open_rate_median?: number | null;
    open_rate_drop_ratio?: number;
    consecutive_healthy_campaigns?: number;
    recovery_campaign_count?: number;
  };
  summary_title: string;
  summary_message: string;
  summary_points: string[];
};

export type CampaignFormState = {
  name: string;
  subject: string;
  scheduled_at: string;
  raw_recipients: string;
  contact_group_ids: number[];
  contact_group_labels: string[];
  html_body: string;
  tracking_enabled: boolean;
  unsubscribe_enabled: boolean;
};

export type ComposerContentMode = "assistant" | "richText" | "layout";

export type AssistantTemplateId = "digest" | "story" | "events";
export type AssistantNewsletterStyle = "modern" | "classic" | "festive" | "editorial" | "minimal" | "bulletin";

export type AssistantSectionLayout = "left" | "right" | "stacked";

export type AssistantThemeSettings = {
  style: AssistantNewsletterStyle;
  primary_color: string;
  accent_color: string;
};

export type AssistantNewsletterStyleOption = {
  value: AssistantNewsletterStyle;
  label: string;
  promise: string;
  preview: [string, string, string];
};

export const ASSISTANT_THEME_STYLE_DEFAULTS: Record<
  AssistantNewsletterStyle,
  Pick<AssistantThemeSettings, "primary_color" | "accent_color">
> = {
  modern: {
    primary_color: "#3152CA",
    accent_color: "#7AA5FF",
  },
  classic: {
    primary_color: "#7B5C36",
    accent_color: "#D8C7A8",
  },
  festive: {
    primary_color: "#8F3538",
    accent_color: "#587286",
  },
  editorial: {
    primary_color: "#28313F",
    accent_color: "#C7D2E5",
  },
  minimal: {
    primary_color: "#1F2A44",
    accent_color: "#DDE3EC",
  },
  bulletin: {
    primary_color: "#C95944",
    accent_color: "#6F88B3",
  },
};

export type AssistantSection = {
  text_align?: NewsletterTextAlignment;
  id: string;
  title: string;
  body: string;
  body_html?: string;
  image_url: string;
  image_alt: string;
  button_label: string;
  button_url: string;
  media_layout: AssistantSectionLayout;
  media_width_ratio: number | null;
  media_height?: number | null;
};

export type AssistantDraftState = {
  text_align?: NewsletterTextAlignment;
  native_design_defaults?: {
    style: AssistantThemeSettings["style"];
    media_layout: AssistantSectionLayout;
    intro_image_layout: AssistantSectionLayout;
    media_width_ratio: number | null;
    media_height: number;
    legacy?: boolean;
  };
  template_id: AssistantTemplateId;
  theme: AssistantThemeSettings;
  header_logo_url: string;
  header_logo_alt: string;
  intro_kicker: string;
  intro_title: string;
  intro_body: string;
  intro_body_html?: string;
  intro_image_url: string;
  intro_image_alt: string;
  intro_image_layout: AssistantSectionLayout;
  intro_image_width_ratio: number | null;
  intro_image_height?: number | null;
  sections: AssistantSection[];
  closing_note: string;
  closing_note_html?: string;
};

type AssistantDraftInput = Partial<Omit<AssistantDraftState, "sections">> & {
  sections?: Array<Partial<AssistantSection> | null | undefined>;
};

export type LayoutContentMode = "grapesjs";

export type StatusTone = "neutral" | "error" | "success";

export type StatusState = {
  message: string;
  tone: StatusTone;
};

export const EMAIL_REGEX = /[A-Z0-9._%+\-']+@[A-Z0-9.\-]+\.[A-Z]{2,}/gi;
export const STRICT_EMAIL_REGEX = /^[A-Z0-9._%+\-']+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;
export const EMAIL_TOKEN_SPLIT_REGEX = /[\s,;\t\r\n]+/;
export const MAX_CAMPAIGN_RECIPIENTS = 3000;
export const SETTINGS_DRAFT_STORAGE_KEY = "easy-newsletter:settings-draft";
export const TEST_RECIPIENT_STORAGE_KEY = "easy-newsletter:test-recipient";
export const ASSISTANT_THEME_DEFAULTS_STORAGE_KEY = "easy-newsletter:assistant-theme-defaults";
export const ASSISTANT_LOGO_DEFAULTS_STORAGE_KEY = "easy-newsletter:assistant-logo-defaults";
export const DEFAULT_CAMPAIGN_HTML = "<p>Bonjour,</p><p>Voici notre newsletter.</p>";
const ASSISTANT_METADATA_PREFIX = "<!--easy-newsletter:assistant:";
const LAYOUT_METADATA_PREFIX = "<!--easy-newsletter:layout-mjml:";
const GENERATED_DRAFT_NAME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const settingsFields = [
  "smtp_host",
  "smtp_port",
  "smtp_security",
  "smtp_username",
  "smtp_password",
  "imap_host",
  "imap_port",
  "imap_ssl",
  "imap_username",
  "imap_password",
  "imap_folder",
  "sender_name",
  "sender_email",
  "reply_to_email",
  "signature_html",
  "public_base_url",
  "default_batch_size",
  "default_batch_delay_seconds",
] as const;

export const campaignFieldIds = [
  "campaign_name",
  "campaign_scheduled_at",
  "campaign_recipients",
  "campaign_subject",
  "campaign_editor",
] as const;

export const ASSISTANT_NEWSLETTER_STYLE_OPTIONS: AssistantNewsletterStyleOption[] = [
  {
    value: "modern",
    label: "Moderne",
    promise: "Clair et professionnel",
    preview: ["#3152CA", "#7AA5FF", "#EEF4FF"],
  },
  {
    value: "classic",
    label: "Classique",
    promise: "Sobre et intemporel",
    preview: ["#7B5C36", "#D8C7A8", "#F6F1E8"],
  },
  {
    value: "festive",
    label: "Festif",
    promise: "Affiche culturelle",
    preview: ["#8F3538", "#E7C84A", "#F2ECE3"],
  },
  {
    value: "editorial",
    label: "Editorial",
    promise: "Elégant et centré contenu",
    preview: ["#28313F", "#C7D2E5", "#F4F5F7"],
  },
  {
    value: "minimal",
    label: "Minimal",
    promise: "Pur et très lisible",
    preview: ["#1F2A44", "#DDE3EC", "#FAFBFC"],
  },
  {
    value: "bulletin",
    label: "Bulletin",
    promise: "Colore et local",
    preview: ["#C95944", "#6F88B3", "#FFF7EA"],
  },
];

export const EMAIL_PROVIDER_PRESETS: EmailProviderPreset[] = [
  {
    id: "infomaniak",
    label: "Infomaniak",
    description: "Serveurs préremplis, il reste surtout l'identifiant et le mot de passe.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/infomaniak.svg",
    brand_color: "#0098ff",
    smtp_host: "mail.infomaniak.com",
    smtp_port: "587",
    smtp_security: "starttls",
    imap_host: "mail.infomaniak.com",
    imap_port: "993",
    imap_ssl: "true",
    smtp_host_aliases: ["smtp.infomaniak.com"],
  },
  {
    id: "gmail",
    label: "Gmail",
    description: "Connexion Gmail en préparation.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/gmail.svg",
    brand_color: "#ea4335",
    smtp_host: "smtp.gmail.com",
    smtp_port: "587",
    smtp_security: "starttls",
    imap_host: "imap.gmail.com",
    imap_port: "993",
    imap_ssl: "true",
    note: "Connexion sécurisée recommandée.",
  },
  {
    id: "outlook",
    label: "Outlook / Microsoft 365",
    description: "Connexion Outlook / Microsoft 365 en préparation.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/microsoft.svg",
    brand_color: "#5e5e5e",
    smtp_host: "smtp-mail.outlook.com",
    smtp_port: "587",
    smtp_security: "starttls",
    imap_host: "outlook.office365.com",
    imap_port: "993",
    imap_ssl: "true",
    note: "Connexion sécurisée recommandée.",
  },
  {
    id: "yahoo",
    label: "Yahoo Mail",
    description: "Réglages IMAP/SMTP Yahoo.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/yahoo.svg",
    brand_color: "#6001d2",
    smtp_host: "smtp.mail.yahoo.com",
    smtp_port: "587",
    smtp_security: "starttls",
    imap_host: "imap.mail.yahoo.com",
    imap_port: "993",
    imap_ssl: "true",
    note: "Mot de passe d'application recommandé.",
  },
  {
    id: "icloud",
    label: "iCloud Mail",
    description: "Réglages IMAP/SMTP iCloud.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/apple.svg",
    brand_color: "#111111",
    smtp_host: "smtp.mail.me.com",
    smtp_port: "587",
    smtp_security: "starttls",
    imap_host: "imap.mail.me.com",
    imap_port: "993",
    imap_ssl: "true",
    note: "Mot de passe d'application recommandé.",
  },
  {
    id: "aol",
    label: "AOL",
    description: "Réglages IMAP/SMTP AOL.",
    icon_url: "https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/aol.svg",
    brand_color: "#3399ff",
    smtp_host: "smtp.aol.com",
    smtp_port: "465",
    smtp_security: "ssl",
    imap_host: "imap.aol.com",
    imap_port: "993",
    imap_ssl: "true",
    imap_host_aliases: ["export.imap.aol.com"],
    note: "AOL demande parfois OAuth2 ou un mot de passe d'application.",
  },
];

const MIN_ASSISTANT_SECTION_MEDIA_RATIO = 0.2;
const MAX_ASSISTANT_SECTION_MEDIA_RATIO = 0.55;
const DEFAULT_ASSISTANT_SECTION_MEDIA_RATIO = 0.3;

export function apiUrl(path: string) {
  return `api/${String(path || "").replace(/^\/+/, "")}`;
}

export function createEmptySettingsForm(): NewsletterSettings {
  return {
    smtp_host: "",
    smtp_port: "587",
    smtp_security: "starttls",
    smtp_username: "",
    smtp_password: "",
    imap_host: "",
    imap_port: "993",
    imap_ssl: "true",
    imap_username: "",
    imap_password: "",
    imap_folder: "INBOX",
    sender_name: "",
    sender_email: "",
    reply_to_email: "",
    auth_method: "",
    oauth_provider: "",
    oauth_connected_email: "",
    oauth_connected_at: "",
    oauth_connection_mode: "",
    email_provider_selection: "",
    email_account_kind: "unknown",
    email_onboarding_previous_sending_route: "",
    email_onboarding_previous_send_size_range: "",
    email_onboarding_previous_send_frequency: "",
    email_onboarding_intended_send_size_range: "",
    email_onboarding_intended_send_frequency: "",
    email_onboarding_intended_email_type: "",
    email_onboarding_recommended_route: "",
    email_onboarding_chosen_route: "",
    email_onboarding_initial_guardrail_profile: "",
    email_onboarding_declined_ses_recommendation_at: "",
    email_route_type: "",
    ses_identity_mode: "dedicated_subdomain",
    ses_domain_identity: "",
    ses_mail_from_domain: "",
    ses_region: "",
    ses_status: "",
    ses_dns_records_json: "",
    ses_dkim_verified: "false",
    ses_mail_from_verified: "false",
    ses_last_checked_at: "",
    ses_last_error: "",
    ses_setup_guidance: "",
    ses_setup_warning: "",
    signature_html: "",
    public_base_url: "",
    default_batch_size: "100",
    default_batch_delay_seconds: "30",
  };
}

export function normalizeSettingsForm(value?: Partial<NewsletterSettings>): NewsletterSettings {
  return {
    ...createEmptySettingsForm(),
    ...(value || {}),
  };
}

export function hasConfiguredSmtpSettings(settings?: Partial<NewsletterSettings>): boolean {
  const normalizedSettings = normalizeSettingsForm(settings);
  if (
    normalizedSettings.auth_method === "oauth" &&
    normalizedSettings.oauth_provider.trim() &&
    normalizedSettings.oauth_connected_email.trim() &&
    normalizedSettings.smtp_host.trim() &&
    normalizedSettings.smtp_username.trim()
  ) {
    return true;
  }
  return ["smtp_host", "smtp_username", "smtp_password"].every((field) =>
    Boolean(String(normalizedSettings[field as keyof NewsletterSettings] || "").trim())
  );
}

export function getEmailProviderPreset(providerId: Exclude<EmailProviderPresetId, "custom">) {
  return EMAIL_PROVIDER_PRESETS.find((preset) => preset.id === providerId) || null;
}

export function detectEmailProviderPreset(settings?: Partial<NewsletterSettings>): EmailProviderPresetId {
  const normalizedSettings = normalizeSettingsForm(settings);

  for (const preset of EMAIL_PROVIDER_PRESETS) {
    const smtpHosts = [preset.smtp_host, ...(preset.smtp_host_aliases || [])].map((value) => value.toLowerCase());
    const imapHosts = [preset.imap_host, ...(preset.imap_host_aliases || [])].map((value) => value.toLowerCase());
    const smtpHost = normalizedSettings.smtp_host.trim().toLowerCase();
    const imapHost = normalizedSettings.imap_host.trim().toLowerCase();

    if (
      smtpHosts.includes(smtpHost) &&
      imapHosts.includes(imapHost) &&
      normalizedSettings.smtp_port.trim() === preset.smtp_port &&
      normalizedSettings.smtp_security.trim().toLowerCase() === preset.smtp_security &&
      normalizedSettings.imap_port.trim() === preset.imap_port &&
      normalizedSettings.imap_ssl.trim().toLowerCase() === preset.imap_ssl
    ) {
      return preset.id;
    }
  }

  return "custom";
}

export function applyEmailProviderPreset(
  settings: Partial<NewsletterSettings>,
  providerId: Exclude<EmailProviderPresetId, "custom">
): NewsletterSettings {
  const preset = getEmailProviderPreset(providerId);
  const normalizedSettings = normalizeSettingsForm(settings);
  if (!preset) {
    return normalizedSettings;
  }

  return {
    ...normalizedSettings,
    smtp_host: preset.smtp_host,
    smtp_port: preset.smtp_port,
    smtp_security: preset.smtp_security,
    imap_host: preset.imap_host,
    imap_port: preset.imap_port,
    imap_ssl: preset.imap_ssl,
    imap_folder: normalizedSettings.imap_folder || "INBOX",
  };
}

export function createEmptyCampaignForm(): CampaignFormState {
  return {
    name: "",
    subject: "",
    scheduled_at: "",
    raw_recipients: "",
    contact_group_ids: [],
    contact_group_labels: [],
    html_body: DEFAULT_CAMPAIGN_HTML,
    tracking_enabled: false,
    unsubscribe_enabled: true,
  };
}

function createAssistantSectionId() {
  return `assistant-section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createEmptyAssistantSection(overrides: Partial<AssistantSection> = {}): AssistantSection {
  return {
    text_align: normalizeNewsletterTextAlignment(overrides.text_align),
    id: overrides.id || createAssistantSectionId(),
    title: overrides.title || "",
    body: overrides.body_html ? newsletterHtmlToText(overrides.body_html) : overrides.body || "",
    body_html: sanitizeNewsletterHtml(overrides.body_html),
    image_url: overrides.image_url || "",
    image_alt: overrides.image_alt || "",
    button_label: overrides.button_label || "",
    button_url: overrides.button_url || "",
    media_layout: overrides.media_layout || "left",
    media_width_ratio: clampAssistantSectionMediaRatio(overrides.media_width_ratio),
    media_height: normalizeNewsletterImageHeight(overrides.media_height),
  };
}

export function hasAssistantNewsletterMetadata(htmlBody: string) {
  return Boolean(String(htmlBody || "").match(/^\s*<!--easy-newsletter:assistant:[A-Za-z0-9+/=]+-->/));
}

export function createAssistantDraftFromDefaultDesign(
  design: NewsletterDefaultDesignSnapshot | null | undefined,
  overrides: Partial<AssistantDraftState> = {}
) {
  const safeDesign = normalizeDefaultDesign(design);
  if (!safeDesign) return createEmptyAssistantDraft(overrides);
  const base = createEmptyAssistantDraft({ ...overrides, template_id: safeDesign.template_id, theme: safeDesign.theme });
  return createEmptyAssistantDraft({
    ...base,
    text_align: safeDesign.text_align,
    header_logo_url: safeDesign.header_logo_url,
    header_logo_alt: safeDesign.header_logo_alt,
    intro_image_url: safeDesign.intro_image_url,
    intro_image_alt: safeDesign.intro_image_alt,
    intro_image_layout: safeDesign.intro_image_layout ?? base.intro_image_layout,
    intro_image_width_ratio: safeDesign.intro_image_width_ratio ?? base.intro_image_width_ratio,
    intro_image_height: safeDesign.intro_image_height ?? base.intro_image_height,
    sections: base.sections.map((section, index) => ({
      ...section,
      ...safeDesign.sections[index % Math.max(1, safeDesign.sections.length)],
    })),
    closing_note: safeDesign.closing_note,
    closing_note_html: safeDesign.closing_note_html,
  });
}

export function resolveDefaultAssistantSectionLayout(theme: Partial<AssistantThemeSettings> | null | undefined): AssistantSectionLayout {
  const normalizedTheme = normalizeAssistantTheme(theme);
  return normalizedTheme.style === "festive" || normalizedTheme.style === "bulletin" ? "stacked" : "left";
}

export function resolveAssistantImageLayoutsForThemeChange(
  draft: AssistantDraftState,
  patch: Partial<AssistantThemeSettings>
): Pick<AssistantDraftState, "sections" | "intro_image_layout"> {
  if (!patch.style || patch.style === draft.theme.style) {
    return { sections: draft.sections, intro_image_layout: draft.intro_image_layout };
  }
  const previousLayout = resolveDefaultAssistantSectionLayout(draft.theme);
  const recorded = draft.native_design_defaults?.style === draft.theme.style ? draft.native_design_defaults : undefined;
  const nextLayout = resolveDefaultAssistantSectionLayout({ ...draft.theme, ...patch });
  const inherited = (layout: AssistantSectionLayout, nativeLayout: AssistantSectionLayout) =>
    layout === nativeLayout || (recorded?.legacy && draft.theme.style === "festive" && layout === "left");
  return {
    sections: draft.sections.map((section) =>
      inherited(section.media_layout, recorded?.media_layout ?? previousLayout)
        ? { ...section, media_layout: nextLayout }
        : section
    ),
    intro_image_layout: inherited(draft.intro_image_layout, recorded?.intro_image_layout ?? previousLayout)
      ? nextLayout
      : draft.intro_image_layout,
  };
}

export function resolveAssistantSectionsForThemeChange(draft: AssistantDraftState, patch: Partial<AssistantThemeSettings>) {
  return resolveAssistantImageLayoutsForThemeChange(draft, patch).sections;
}

export function createEmptyAssistantDraft(overrides: Partial<AssistantDraftState> = {}): AssistantDraftState {
  const normalizedTheme = normalizeAssistantTheme(overrides.theme);
  const defaultMediaLayout = resolveDefaultAssistantSectionLayout(normalizedTheme);
  const sections =
    overrides.sections && overrides.sections.length > 0
      ? overrides.sections.map((section) =>
          createEmptyAssistantSection({
            ...section,
            media_layout: section.media_layout || defaultMediaLayout,
          })
        )
      : [createEmptyAssistantSection({ media_layout: defaultMediaLayout })];

  return {
    native_design_defaults: overrides.native_design_defaults?.style === normalizedTheme.style
      ? overrides.native_design_defaults
      : { style: normalizedTheme.style, media_layout: defaultMediaLayout, intro_image_layout: defaultMediaLayout, media_width_ratio: null, media_height: normalizeNewsletterImageHeight(undefined) },
    text_align: normalizeNewsletterTextAlignment(overrides.text_align) || "left",
    template_id: overrides.template_id || "digest",
    theme: normalizedTheme,
    header_logo_url: overrides.header_logo_url || "",
    header_logo_alt: overrides.header_logo_alt || "",
    intro_kicker: overrides.intro_kicker || "",
    intro_title: overrides.intro_title || "",
    intro_body: overrides.intro_body_html ? newsletterHtmlToText(overrides.intro_body_html) : overrides.intro_body || "",
    intro_body_html: sanitizeNewsletterHtml(overrides.intro_body_html),
    intro_image_url: overrides.intro_image_url || "",
    intro_image_alt: overrides.intro_image_alt || "",
    intro_image_layout:
      overrides.intro_image_layout === "left" || overrides.intro_image_layout === "right" || overrides.intro_image_layout === "stacked"
        ? overrides.intro_image_layout
        : defaultMediaLayout,
    intro_image_width_ratio: clampAssistantSectionMediaRatio(overrides.intro_image_width_ratio),
    intro_image_height: normalizeNewsletterImageHeight(overrides.intro_image_height),
    sections,
    closing_note: overrides.closing_note_html ? newsletterHtmlToText(overrides.closing_note_html) : overrides.closing_note || "",
    closing_note_html: sanitizeNewsletterHtml(overrides.closing_note_html),
  };
}

export function createAssistantDraftFromCampaignForm(form: Partial<CampaignFormState>): AssistantDraftState {
  return createEmptyAssistantDraft({
    header_logo_url: "",
    header_logo_alt: "",
    intro_kicker: "",
    intro_title: "",
    intro_body: "",
    intro_image_url: "",
    intro_image_alt: "",
  });
}

export function toCampaignForm(campaign: Partial<NewsletterCampaign>): CampaignFormState {
  return {
    name: campaign.name || "",
    subject: campaign.subject || "",
    scheduled_at: campaign.scheduled_at || "",
    raw_recipients: campaign.raw_recipients || "",
    contact_group_ids: Array.isArray(campaign.contact_group_ids) ? campaign.contact_group_ids : [],
    contact_group_labels: Array.isArray(campaign.contact_group_labels) ? campaign.contact_group_labels : [],
    html_body: campaign.html_body || DEFAULT_CAMPAIGN_HTML,
    tracking_enabled: Boolean(campaign.tracking_enabled),
    unsubscribe_enabled: Boolean(campaign.unsubscribe_enabled),
  };
}

function encodeAssistantDraft(value: AssistantDraftState) {
  const json = JSON.stringify(value);
  return encodeComposerMetadata(json);
}

function decodeAssistantDraft(value: string) {
  const json = decodeComposerMetadata(value);
  return JSON.parse(json) as Partial<AssistantDraftState>;
}

function encodeComposerMetadata(value: string) {
  return globalThis.btoa(unescape(encodeURIComponent(value)));
}

function decodeComposerMetadata(value: string) {
  return decodeURIComponent(escape(globalThis.atob(value)));
}

function normalizeAssistantSection(
  value: Partial<AssistantSection> | null | undefined,
  defaultLayout: AssistantSectionLayout = "left"
) {
  return createEmptyAssistantSection({
    text_align: normalizeNewsletterTextAlignment(value?.text_align),
    id: value?.id,
    title: value?.title,
    body: value?.body,
    body_html: value?.body_html,
    image_url: value?.image_url,
    image_alt: value?.image_alt,
    button_label: value?.button_label,
    button_url: value?.button_url,
    media_layout:
      value?.media_layout === "left" || value?.media_layout === "right" || value?.media_layout === "stacked"
        ? value.media_layout
        : defaultLayout,
    media_width_ratio: value?.media_width_ratio,
    media_height: value?.media_height,
  });
}

export function createDefaultAssistantTheme(overrides: Partial<AssistantThemeSettings> = {}): AssistantThemeSettings {
  const style = (overrides.style || "modern") as AssistantNewsletterStyle;
  return {
    style,
    primary_color: typeof overrides.primary_color === "string" ? overrides.primary_color : "",
    accent_color: typeof overrides.accent_color === "string" ? overrides.accent_color : "",
  };
}

export function normalizeAssistantTheme(value: Partial<AssistantThemeSettings> | null | undefined) {
  const nextTheme = createDefaultAssistantTheme(value || {});
  if (!["modern", "classic", "festive", "editorial", "minimal", "bulletin"].includes(nextTheme.style)) {
    nextTheme.style = "modern";
  }
  if (nextTheme.primary_color && !/^#[0-9a-f]{6}$/i.test(nextTheme.primary_color)) {
    nextTheme.primary_color = "";
  }
  if (nextTheme.accent_color && !/^#[0-9a-f]{6}$/i.test(nextTheme.accent_color)) {
    nextTheme.accent_color = "";
  }
  return nextTheme;
}

export function resolveAssistantThemeColors(theme: Partial<AssistantThemeSettings> | null | undefined) {
  const normalizedTheme = normalizeAssistantTheme(theme);
  const styleDefaults = ASSISTANT_THEME_STYLE_DEFAULTS[normalizedTheme.style];
  return {
    primary_color: normalizedTheme.primary_color || styleDefaults.primary_color,
    accent_color: normalizedTheme.accent_color || styleDefaults.accent_color,
  };
}

export function normalizeAssistantDraft(value: AssistantDraftInput | null | undefined) {
  return createEmptyAssistantDraft({
    text_align: normalizeNewsletterTextAlignment(value?.text_align),
    native_design_defaults: value?.native_design_defaults,
    template_id:
      value?.template_id === "story" || value?.template_id === "events" ? value.template_id : "digest",
    theme: normalizeAssistantTheme(value?.theme),
    header_logo_url: value?.header_logo_url,
    header_logo_alt: value?.header_logo_alt,
    intro_kicker: value?.intro_kicker,
    intro_title: value?.intro_title,
    intro_body: value?.intro_body,
    intro_body_html: value?.intro_body_html,
    intro_image_url: value?.intro_image_url,
    intro_image_alt: value?.intro_image_alt,
    intro_image_layout:
      value?.intro_image_layout === "left" || value?.intro_image_layout === "right" || value?.intro_image_layout === "stacked"
        ? value.intro_image_layout
        : undefined,
    intro_image_width_ratio: value?.intro_image_width_ratio,
    intro_image_height: value?.intro_image_height,
    sections: Array.isArray(value?.sections)
      ? value.sections.map((section) => normalizeAssistantSection(section, resolveDefaultAssistantSectionLayout(value?.theme)))
      : undefined,
    closing_note: value?.closing_note,
    closing_note_html: value?.closing_note_html,
  });
}

export function assistantDraftHasMeaningfulContent(value: AssistantDraftInput | null | undefined) {
  const draft = normalizeAssistantDraft(value);
  return Boolean(
    draft.header_logo_url.trim() ||
      draft.intro_kicker.trim() ||
      draft.intro_title.trim() ||
      draft.intro_body.trim() ||
      draft.intro_image_url.trim() ||
      draft.closing_note.trim() ||
      draft.sections.some((section) =>
        Boolean(
          section.title.trim() ||
            section.body.trim() ||
            section.image_url.trim() ||
            section.image_alt.trim() ||
            section.button_label.trim() ||
            section.button_url.trim()
        )
      )
  );
}

export function classicComposerHasMeaningfulContent(htmlBody: string) {
  const strippedHtml = stripComposerMetadata(htmlBody).trim();
  if (!editorHtmlHasContent(strippedHtml)) {
    return false;
  }

  return strippedHtml !== DEFAULT_CAMPAIGN_HTML;
}

function escapePlainEmailText(value: string) {
  return escapeHtml(value).replace(/\r\n|\r|\n/g, "<br />");
}

export function buildPlainClassicEmailHtmlFromAssistantDraft(value: AssistantDraftInput | null | undefined) {
  const draft = normalizeAssistantDraft(value);
  const blocks: string[] = [];

  if (draft.intro_kicker.trim()) {
    blocks.push(
      `<p style="font-size:13px;line-height:1.4;font-weight:700;margin:0 0 8px;">${escapePlainEmailText(draft.intro_kicker.trim())}</p>`
    );
  }

  if (draft.intro_title.trim()) {
    blocks.push(
      `<h1 style="font-size:24px;line-height:1.25;font-weight:700;margin:0 0 16px;">${escapePlainEmailText(draft.intro_title.trim())}</h1>`
    );
  }

  if (draft.intro_body.trim()) {
    blocks.push(
      `<p style="font-size:16px;line-height:1.6;margin:0 0 16px;">${escapePlainEmailText(draft.intro_body.trim())}</p>`
    );
  }

  draft.sections.forEach((section) => {
    if (section.title.trim()) {
      blocks.push(
        `<h2 style="font-size:20px;line-height:1.3;font-weight:700;margin:24px 0 10px;">${escapePlainEmailText(section.title.trim())}</h2>`
      );
    }

    if (section.body.trim()) {
      blocks.push(
        `<p style="font-size:16px;line-height:1.6;margin:0 0 16px;">${escapePlainEmailText(section.body.trim())}</p>`
      );
    }

    if (section.button_label.trim() && section.button_url.trim()) {
      blocks.push(
        `<p style="font-size:16px;line-height:1.6;font-weight:700;margin:0 0 16px;"><a href="${escapeHtml(section.button_url.trim())}">${escapePlainEmailText(section.button_label.trim())}</a></p>`
      );
    }
  });

  if (draft.closing_note.trim()) {
    blocks.push(
      `<p style="font-size:16px;line-height:1.6;margin:24px 0 0;">${escapePlainEmailText(draft.closing_note.trim())}</p>`
    );
  }

  return blocks.join("\n");
}

export function buildClassicCopyName(name: string) {
  const trimmedName = String(name || "").trim();
  if (!trimmedName) {
    return "Newsletter classique";
  }
  if (/\(classique\)$/i.test(trimmedName)) {
    return trimmedName;
  }
  return `${trimmedName} (classique)`;
}

export function buildGeneratedDraftName(date = new Date()) {
  const formatted = GENERATED_DRAFT_NAME_FORMATTER.format(date).replace(",", "");
  return `Newsletter du ${formatted}`;
}

export function readAssistantThemeDefaults() {
  try {
    const rawValue = globalThis.localStorage?.getItem(ASSISTANT_THEME_DEFAULTS_STORAGE_KEY);
    if (!rawValue) {
      return createDefaultAssistantTheme();
    }
    return normalizeAssistantTheme(JSON.parse(rawValue) as Partial<AssistantThemeSettings>);
  } catch {
    return createDefaultAssistantTheme();
  }
}

export function readAssistantLogoDefaults() {
  try {
    const rawValue = globalThis.localStorage?.getItem(ASSISTANT_LOGO_DEFAULTS_STORAGE_KEY);
    if (!rawValue) {
      return {
        header_logo_url: "",
        header_logo_alt: "",
      };
    }

    const parsedValue = JSON.parse(rawValue) as Partial<Pick<AssistantDraftState, "header_logo_url" | "header_logo_alt">>;
    return {
      header_logo_url: typeof parsedValue.header_logo_url === "string" ? parsedValue.header_logo_url : "",
      header_logo_alt: typeof parsedValue.header_logo_alt === "string" ? parsedValue.header_logo_alt : "",
    };
  } catch {
    return {
      header_logo_url: "",
      header_logo_alt: "",
    };
  }
}

export function persistAssistantThemeDefaults(value: Partial<AssistantThemeSettings> | null | undefined) {
  try {
    globalThis.localStorage?.setItem(
      ASSISTANT_THEME_DEFAULTS_STORAGE_KEY,
      JSON.stringify(normalizeAssistantTheme(value))
    );
  } catch {
    // localStorage can be unavailable in embedded contexts; keep defaults in-memory only.
  }
}

export function persistAssistantLogoDefaults(
  value: Partial<Pick<AssistantDraftState, "header_logo_url" | "header_logo_alt">> | null | undefined
) {
  try {
    globalThis.localStorage?.setItem(
      ASSISTANT_LOGO_DEFAULTS_STORAGE_KEY,
      JSON.stringify({
        header_logo_url: typeof value?.header_logo_url === "string" ? value.header_logo_url : "",
        header_logo_alt: typeof value?.header_logo_alt === "string" ? value.header_logo_alt : "",
      })
    );
  } catch {
    // localStorage can be unavailable in embedded contexts; keep defaults in-memory only.
  }
}

function hexToRgb(color: string) {
  const normalized = color.replace("#", "");
  const safeColor = normalized.length === 6 ? normalized : "3152CA";
  return {
    r: Number.parseInt(safeColor.slice(0, 2), 16),
    g: Number.parseInt(safeColor.slice(2, 4), 16),
    b: Number.parseInt(safeColor.slice(4, 6), 16),
  };
}

function parsePixelValue(value: string, fallback: number) {
  const match = String(value || "").match(/(-?\d+(?:\.\d+)?)px/);
  return match ? Number.parseFloat(match[1]) : fallback;
}

export function clampAssistantSectionMediaRatio(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }

  const clampedValue = Math.min(MAX_ASSISTANT_SECTION_MEDIA_RATIO, Math.max(MIN_ASSISTANT_SECTION_MEDIA_RATIO, value));
  return Math.round(clampedValue * 1000) / 1000;
}

export function resolveDefaultAssistantSectionMediaRatio(theme: Partial<AssistantThemeSettings> | null | undefined) {
  const tokens = resolveAssistantThemeTokens(theme);
  const containerWidth = parsePixelValue(tokens.containerWidth, 680);
  const mediaWidth = parsePixelValue(tokens.mediaWidth, 180);
  const sectionGap = parsePixelValue(tokens.sectionGap, 16);
  const availableWidth = Math.max(containerWidth - sectionGap, 320);

  return (
    clampAssistantSectionMediaRatio(mediaWidth / availableWidth) || DEFAULT_ASSISTANT_SECTION_MEDIA_RATIO
  );
}

export function resolveAssistantSectionMediaRatio(
  section: Partial<AssistantSection> | null | undefined,
  theme: Partial<AssistantThemeSettings> | null | undefined
) {
  return clampAssistantSectionMediaRatio(section?.media_width_ratio) || resolveDefaultAssistantSectionMediaRatio(theme);
}

function withAlpha(color: string, alpha: number) {
  const { r, g, b } = hexToRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mixHex(colorA: string, colorB: string, ratio: number) {
  const clampedRatio = Math.min(1, Math.max(0, ratio));
  const left = hexToRgb(colorA);
  const right = hexToRgb(colorB);
  const mixChannel = (a: number, b: number) => Math.round(a * (1 - clampedRatio) + b * clampedRatio);
  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(mixChannel(left.r, right.r))}${toHex(mixChannel(left.g, right.g))}${toHex(mixChannel(left.b, right.b))}`;
}

function lighten(color: string, amount: number) {
  return mixHex(color, "#ffffff", amount);
}

function darken(color: string, amount: number) {
  return mixHex(color, "#101318", amount);
}

function relativeLuminance(color: string) {
  const { r, g, b } = hexToRgb(color);
  const normalize = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
}

function contrastText(color: string, dark = "#182132", light = "#ffffff") {
  return relativeLuminance(color) > 0.55 ? dark : light;
}

type AssistantThemeTokens = {
  primary: string;
  accent: string;
  secondary: string;
  canvas: string;
  card: string;
  border: string;
  text: string;
  muted: string;
  primaryContrastText: string;
  secondaryContrastText: string;
  buttonText: string;
  heroText: string;
  radius: string;
  headingFont: string;
  bodyFont: string;
  kickerSpacing: string;
  heroScale: number;
  buttonRadius: string;
  heroBackground: string;
  heroBodyText: string;
  buttonBackground: string;
  buttonBorder: string;
  buttonMode: "solid" | "outline" | "ghost";
  canvasPadding: string;
  cardPadding: string;
  sectionGap: string;
  cardShadow: string;
  containerWidth: string;
  heroBorder: string;
  heroShadow: string;
  buttonShadow: string;
  buttonTextDecoration: string;
  heroTitleSize: string;
  sectionTitleSize: string;
  bodySize: string;
  bodyLineHeight: string;
  mediaWidth: string;
  imageMinHeight: string;
  imageStackedHeight: string;
};

export type AssistantBulletinTone = {
  background: string;
  text: string;
  surface: string;
  surfaceText: string;
};

export type AssistantBulletinPalette = {
  heroBanner: string;
  heroBannerText: string;
  heroAccent: string;
  heroAccentText: string;
  paper: string;
  paperStrong: string;
  ink: string;
  muted: string;
  border: string;
  sectionTones: AssistantBulletinTone[];
};

export function resolveAssistantBulletinPalette(
  theme: Partial<AssistantThemeSettings> | null | undefined
): AssistantBulletinPalette {
  const resolvedColors = resolveAssistantThemeColors(theme);
  const primary = resolvedColors.primary_color;
  const accent = resolvedColors.accent_color;
  const warm = mixHex(primary, "#E8BF56", 0.78);
  const orange = mixHex(primary, "#DF8D3C", 0.66);
  const support = mixHex(accent, "#8DAA62", 0.58);
  const paper = "#FAF4E7";
  const paperStrong = "#FFFAF1";
  const ink = "#2F2B26";
  const muted = "#686159";
  const border = "rgba(47, 43, 38, 0.16)";
  const makeTone = (background: string): AssistantBulletinTone => ({
    background,
    text: contrastText(background, ink, "#FFF9F0"),
    surface: lighten(background, 0.78),
    surfaceText: darken(background, 0.42),
  });

  return {
    heroBanner: primary,
    heroBannerText: contrastText(primary, ink, "#FFF9F0"),
    heroAccent: warm,
    heroAccentText: contrastText(warm, ink, "#FFF9F0"),
    paper,
    paperStrong,
    ink,
    muted,
    border,
    sectionTones: [makeTone(accent), makeTone(orange), makeTone(support), makeTone(warm), makeTone(primary)],
  };
}

export function resolveAssistantThemeTokens(theme: Partial<AssistantThemeSettings> | null | undefined): AssistantThemeTokens {
  const normalizedTheme = normalizeAssistantTheme(theme);
  const resolvedColors = resolveAssistantThemeColors(normalizedTheme);
  const primary = resolvedColors.primary_color;
  const secondary = resolvedColors.accent_color;
  const accent = normalizedTheme.style === "festive" ? "#E7C84A" : resolvedColors.accent_color;
  const bulletinPalette = resolveAssistantBulletinPalette(normalizedTheme);
  const primaryContrastText = contrastText(primary, "#1E1E1E", "#F8FBFF");
  const secondaryContrastText = contrastText(secondary, "#1E1E1E", "#F8FBFF");
  const presets: Record<
    AssistantNewsletterStyle,
    Omit<AssistantThemeTokens, "primary" | "accent" | "secondary" | "primaryContrastText" | "secondaryContrastText">
  > = {
    modern: {
      canvas: lighten(mixHex(primary, accent, 0.28), 0.9),
      card: "#FFFFFF",
      border: withAlpha(darken(primary, 0.08), 0.16),
      text: "#1F2A44",
      muted: "#52617E",
      buttonText: primaryContrastText,
      heroText: "#FFFFFF",
      radius: "24px",
      headingFont: "'Trebuchet MS', 'Avenir Next', Arial, sans-serif",
      bodyFont: "Arial, 'Helvetica Neue', sans-serif",
      kickerSpacing: "0.12em",
      heroScale: 1,
      buttonRadius: "999px",
      heroBackground: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
      heroBodyText: "rgba(255,255,255,0.92)",
      buttonBackground: primary,
      buttonBorder: "transparent",
      buttonMode: "solid",
      canvasPadding: "22px",
      cardPadding: "22px 24px",
      sectionGap: "16px",
      cardShadow: `0 16px 34px ${withAlpha(primary, 0.08)}`,
      containerWidth: "680px",
      heroBorder: withAlpha(lighten(primary, 0.15), 0.28),
      heroShadow: `0 18px 40px ${withAlpha(primary, 0.18)}`,
      buttonShadow: `0 12px 24px ${withAlpha(primary, 0.18)}`,
      buttonTextDecoration: "none",
      heroTitleSize: "2.18rem",
      sectionTitleSize: "1.56rem",
      bodySize: "1rem",
      bodyLineHeight: "1.72",
      mediaWidth: "180px",
      imageMinHeight: "148px",
      imageStackedHeight: "208px",
    },
    classic: {
      canvas: mixHex(primary, "#f6f0e6", 0.9),
      card: mixHex(primary, "#fffdfa", 0.96),
      border: mixHex(primary, "#d7c2a4", 0.62),
      text: "#453524",
      muted: "#6E5B45",
      buttonText: darken(primary, 0.28),
      heroText: "#FFFDF8",
      radius: "14px",
      headingFont: "Georgia, 'Times New Roman', serif",
      bodyFont: "Georgia, 'Times New Roman', serif",
      kickerSpacing: "0.18em",
      heroScale: 0.94,
      buttonRadius: "10px",
      heroBackground: `linear-gradient(180deg, ${mixHex(primary, "#6a4f36", 0.28)} 0%, ${mixHex(primary, "#402c1c", 0.42)} 100%)`,
      heroBodyText: "rgba(255,248,240,0.9)",
      buttonBackground: "transparent",
      buttonBorder: mixHex(primary, "#7d5e3b", 0.3),
      buttonMode: "outline",
      canvasPadding: "18px",
      cardPadding: "24px 26px",
      sectionGap: "18px",
      cardShadow: "transparent",
      containerWidth: "620px",
      heroBorder: withAlpha(lighten(primary, 0.15), 0.16),
      heroShadow: "none",
      buttonShadow: "none",
      buttonTextDecoration: "none",
      heroTitleSize: "2rem",
      sectionTitleSize: "1.62rem",
      bodySize: "0.99rem",
      bodyLineHeight: "1.8",
      mediaWidth: "168px",
      imageMinHeight: "146px",
      imageStackedHeight: "198px",
    },
    festive: {
      canvas: "#FBF8F3",
      card: "#F2ECE3",
      border: withAlpha(mixHex(primary, secondary, 0.5), 0.24),
      text: "#1E1E1E",
      muted: "#443B34",
      buttonText: primaryContrastText,
      heroText: "#1E1E1E",
      radius: "10px",
      headingFont: "'Arial Black', 'Trebuchet MS', Arial, sans-serif",
      bodyFont: "Arial, 'Helvetica Neue', sans-serif",
      kickerSpacing: "0.2em",
      heroScale: 1.02,
      buttonRadius: "10px",
      heroBackground: "#F2ECE3",
      heroBodyText: "#2A2724",
      buttonBackground: primary,
      buttonBorder: primary,
      buttonMode: "solid",
      canvasPadding: "24px",
      cardPadding: "18px 20px",
      sectionGap: "24px",
      cardShadow: "none",
      containerWidth: "700px",
      heroBorder: accent,
      heroShadow: "none",
      buttonShadow: "none",
      buttonTextDecoration: "underline",
      heroTitleSize: "2.3rem",
      sectionTitleSize: "1.78rem",
      bodySize: "1rem",
      bodyLineHeight: "1.62",
      mediaWidth: "204px",
      imageMinHeight: "168px",
      imageStackedHeight: "232px",
    },
    editorial: {
      canvas: mixHex(primary, "#f4f5f7", 0.92),
      card: mixHex(primary, "#ffffff", 0.975),
      border: withAlpha(darken(primary, 0.18), 0.14),
      text: "#202938",
      muted: "#5E687B",
      buttonText: darken(primary, 0.1),
      heroText: "#FFFFFF",
      radius: "10px",
      headingFont: "Georgia, 'Times New Roman', serif",
      bodyFont: "'Helvetica Neue', Arial, sans-serif",
      kickerSpacing: "0.16em",
      heroScale: 1.02,
      buttonRadius: "4px",
      heroBackground: `linear-gradient(180deg, ${mixHex(primary, "#2a313a", 0.18)} 0%, ${darken(primary, 0.38)} 100%)`,
      heroBodyText: "rgba(255,255,255,0.88)",
      buttonBackground: "transparent",
      buttonBorder: "transparent",
      buttonMode: "ghost",
      canvasPadding: "18px",
      cardPadding: "18px 16px",
      sectionGap: "24px",
      cardShadow: "transparent",
      containerWidth: "640px",
      heroBorder: withAlpha(lighten(primary, 0.12), 0.1),
      heroShadow: "none",
      buttonShadow: "none",
      buttonTextDecoration: "underline",
      heroTitleSize: "2.1rem",
      sectionTitleSize: "1.68rem",
      bodySize: "1.01rem",
      bodyLineHeight: "1.82",
      mediaWidth: "170px",
      imageMinHeight: "142px",
      imageStackedHeight: "190px",
    },
    minimal: {
      canvas: mixHex(primary, "#fafbfc", 0.97),
      card: mixHex(primary, "#ffffff", 0.985),
      border: withAlpha(primary, 0.1),
      text: "#1F2A44",
      muted: "#687487",
      buttonText: darken(primary, 0.06),
      heroText: darken(primary, 0.12),
      radius: "12px",
      headingFont: "'Helvetica Neue', Arial, sans-serif",
      bodyFont: "'Helvetica Neue', Arial, sans-serif",
      kickerSpacing: "0.22em",
      heroScale: 0.9,
      buttonRadius: "999px",
      heroBackground: `linear-gradient(180deg, ${lighten(primary, 0.92)} 0%, ${lighten(accent, 0.95)} 100%)`,
      heroBodyText: darken(primary, 0.05),
      buttonBackground: "transparent",
      buttonBorder: withAlpha(primary, 0.22),
      buttonMode: "outline",
      canvasPadding: "18px",
      cardPadding: "16px 12px",
      sectionGap: "28px",
      cardShadow: "transparent",
      containerWidth: "620px",
      heroBorder: withAlpha(primary, 0.08),
      heroShadow: "none",
      buttonShadow: "none",
      buttonTextDecoration: "none",
      heroTitleSize: "1.84rem",
      sectionTitleSize: "1.44rem",
      bodySize: "0.98rem",
      bodyLineHeight: "1.78",
      mediaWidth: "156px",
      imageMinHeight: "136px",
      imageStackedHeight: "186px",
    },
    bulletin: {
      canvas: "#FFFFFF",
      card: "#FFFFFF",
      border: bulletinPalette.border,
      text: bulletinPalette.ink,
      muted: bulletinPalette.muted,
      buttonText: contrastText(primary, bulletinPalette.ink, "#FFF9F0"),
      heroText: bulletinPalette.heroBannerText,
      radius: "8px",
      headingFont: "Georgia, 'Times New Roman', serif",
      bodyFont: "Arial, 'Helvetica Neue', sans-serif",
      kickerSpacing: "0.14em",
      heroScale: 1,
      buttonRadius: "6px",
      heroBackground: "#FFFFFF",
      heroBodyText: bulletinPalette.muted,
      buttonBackground: primary,
      buttonBorder: primary,
      buttonMode: "solid",
      canvasPadding: "20px",
      cardPadding: "16px 16px",
      sectionGap: "16px",
      cardShadow: "transparent",
      containerWidth: "680px",
      heroBorder: "rgba(47, 43, 38, 0.14)",
      heroShadow: "none",
      buttonShadow: "none",
      buttonTextDecoration: "none",
      heroTitleSize: "2.2rem",
      sectionTitleSize: "1.65rem",
      bodySize: "1rem",
      bodyLineHeight: "1.72",
      mediaWidth: "178px",
      imageMinHeight: "150px",
      imageStackedHeight: "220px",
    },
  };

  return {
    primary,
    accent,
    secondary,
    primaryContrastText,
    secondaryContrastText,
    ...presets[normalizedTheme.style],
  };
}

function escapeHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeMjmlText(value: string) {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function buildAssistantButtonHtml(section: AssistantSection) {
  if (!section.button_label.trim() || !section.button_url.trim()) {
    return "";
  }

  return `
    <div style="margin-top:16px;">
      <a href="${escapeHtml(section.button_url.trim())}" style="display:inline-block;padding:12px 18px;border-radius:999px;background:#4b7552;color:#ffffff;font-weight:700;text-decoration:none;">
        ${escapeHtml(section.button_label.trim())}
      </a>
    </div>
  `;
}

function buildAssistantImageHtml(section: AssistantSection) {
  if (!section.image_url.trim()) {
    return "";
  }

  return `
    <img
      src="${escapeHtml(section.image_url.trim())}"
      alt="${escapeHtml(section.image_alt.trim() || section.title.trim() || "Illustration")}"
      style="display:block;width:100%;max-width:260px;height:auto;border-radius:18px;border:0;"
    />
  `;
}

function buildAssistantSectionHtml(section: AssistantSection) {
  const title = section.title.trim();
  const body = section.body.trim();
  const textHtml = `
    <td style="padding:0;vertical-align:top;">
      ${title ? `<h2 style="margin:0 0 12px;font-size:24px;line-height:1.2;color:#1f2a44;">${escapeHtml(title)}</h2>` : ""}
      ${body ? `<p style="margin:0;color:#52617e;font-size:15px;line-height:1.7;white-space:pre-line;">${escapeHtml(body)}</p>` : ""}
      ${buildAssistantButtonHtml(section)}
    </td>
  `;
  const imageHtml = `
    <td style="padding:0;vertical-align:top;width:280px;">
      ${buildAssistantImageHtml(section)}
    </td>
  `;

  if (section.media_layout === "stacked" || !section.image_url.trim()) {
    return `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="padding:0 0 16px;">
            ${buildAssistantImageHtml(section)}
          </td>
        </tr>
        <tr>
          ${textHtml}
        </tr>
      </table>
    `;
  }

  const firstColumn = section.media_layout === "right" ? textHtml : imageHtml;
  const secondColumn = section.media_layout === "right" ? imageHtml : textHtml;

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="padding:0;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;">
            <tr>
              ${firstColumn}
              <td style="width:24px;padding:0;"></td>
              ${secondColumn}
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
}

export async function buildAssistantHtml(value: AssistantDraftState) {
  const draft = normalizeAssistantDraft(value);
  const html = await renderAssistantEmailHtml(draft);
  return `${ASSISTANT_METADATA_PREFIX}${encodeAssistantDraft(draft)}-->${html}`;
}

export function createDefaultLayoutMjml() {
  return `
    <mjml>
      <mj-body background-color="#eef4ff">
        <mj-section background-color="#1d327f" padding="32px 24px">
          <mj-column>
            <mj-text color="#ffffff" font-size="14px" text-transform="uppercase" letter-spacing="1px">
              Newsletter
            </mj-text>
            <mj-text color="#ffffff" font-size="30px" font-weight="700" line-height="1.2">
              Bonjour,
            </mj-text>
            <mj-text color="#dfe7ff" font-size="16px" line-height="1.7">
              Construisez ici votre mise en page newsletter.
            </mj-text>
          </mj-column>
        </mj-section>
        <mj-section background-color="#ffffff" padding="24px">
          <mj-column>
            <mj-text font-size="20px" font-weight="700" color="#1f2a44">
              Premier bloc
            </mj-text>
            <mj-text font-size="16px" line-height="1.7" color="#52617e">
              Ajoutez ici vos contenus, images et boutons directement depuis l'editeur visuel.
            </mj-text>
          </mj-column>
        </mj-section>
      </mj-body>
    </mjml>
  `.trim();
}

export function createLayoutMjmlFromCampaignForm(form: Partial<CampaignFormState>) {
  const strippedHtml = stripComposerMetadata(form.html_body || "").trim();
  const introLabel = form.name?.trim() || "Newsletter";
  const introTitle = form.subject?.trim() || "Bonjour,";

  if (!editorHtmlHasContent(strippedHtml) || strippedHtml === DEFAULT_CAMPAIGN_HTML) {
    return createDefaultLayoutMjml()
      .replace(">Newsletter<", `>${escapeMjmlText(introLabel)}<`)
      .replace(">Bonjour,<", `>${escapeMjmlText(introTitle)}<`);
  }

  return `
    <mjml>
      <mj-body background-color="#eef4ff">
        <mj-section background-color="#1d327f" padding="32px 24px">
          <mj-column>
            <mj-text color="#ffffff" font-size="14px" text-transform="uppercase" letter-spacing="1px">
              ${escapeMjmlText(introLabel)}
            </mj-text>
            <mj-text color="#ffffff" font-size="30px" font-weight="700" line-height="1.2">
              ${escapeMjmlText(introTitle)}
            </mj-text>
            <mj-text color="#dfe7ff" font-size="16px" line-height="1.7">
              Contenu importé depuis la newsletter existante.
            </mj-text>
          </mj-column>
        </mj-section>
        <mj-section background-color="#ffffff" padding="24px">
          <mj-column>
            <mj-raw>${strippedHtml}</mj-raw>
          </mj-column>
        </mj-section>
      </mj-body>
    </mjml>
  `.trim();
}

export function buildLayoutHtml(html: string, mjml: string) {
  return `${LAYOUT_METADATA_PREFIX}${encodeComposerMetadata(mjml)}-->${String(html || "").trim()}`;
}

export function parseLayoutMjmlFromHtml(htmlBody: string) {
  const match = String(htmlBody || "").match(/^\s*<!--easy-newsletter:layout-mjml:([A-Za-z0-9+/=]+)-->/);
  if (!match || !match[1]) {
    return null;
  }

  try {
    return decodeComposerMetadata(match[1]);
  } catch {
    return null;
  }
}

export function parseAssistantDraftFromHtml(htmlBody: string) {
  const match = String(htmlBody || "").match(/^\s*<!--easy-newsletter:assistant:([A-Za-z0-9+/=]+)-->/);
  if (!match || !match[1]) {
    return null;
  }

  try {
    const decoded = decodeAssistantDraft(match[1]);
    const draft = normalizeAssistantDraft(decoded);
    if (!decoded.native_design_defaults) {
      draft.native_design_defaults = { ...draft.native_design_defaults!, legacy: true };
    }
    return draft;
  } catch {
    return null;
  }
}

export function stripComposerMetadata(htmlBody: string) {
  return String(htmlBody || "").replace(
    /^\s*<!--easy-newsletter:(?:assistant|layout-mjml):[A-Za-z0-9+/=]+-->\s*/,
    ""
  );
}

export function extractEmailPreviewHtml(htmlBody: string) {
  const strippedHtml = stripComposerMetadata(htmlBody).trim();
  if (!strippedHtml) {
    return "";
  }

  try {
    const parsed = new DOMParser().parseFromString(strippedHtml, "text/html");
    const bodyHtml = parsed.body?.innerHTML?.trim() || "";
    return bodyHtml || strippedHtml;
  } catch {
    return strippedHtml;
  }
}

export function buildCampaignPayload(form: CampaignFormState, editingDraftId: number | null, saveAsDraft: boolean) {
  const browserTimezone =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "" : "";
  const payload: Record<string, unknown> = {
    save_as_draft: saveAsDraft,
    name: form.name,
    subject: form.subject,
    scheduled_at: form.scheduled_at,
    browser_timezone: browserTimezone,
    tracking_enabled: form.tracking_enabled,
    unsubscribe_enabled: form.unsubscribe_enabled,
    raw_recipients: form.raw_recipients,
    contact_group_ids: form.contact_group_ids,
    contact_group_labels: form.contact_group_labels,
    html_body: form.html_body,
  };

  if (editingDraftId) {
    payload.campaign_id = editingDraftId;
  }

  return payload;
}

export function buildTestEmailPayload(
  campaignForm: CampaignFormState,
  settingsForm: NewsletterSettings,
  testRecipientEmail: string
) {
  return {
    ...settingsForm,
    name: campaignForm.name,
    subject: campaignForm.subject,
    html_body: campaignForm.html_body,
    test_recipient_email: testRecipientEmail,
  };
}

export function extractEmails(rawValue: string) {
  const matches = rawValue.match(EMAIL_REGEX) || [];
  const unique: string[] = [];
  const seen = new Set<string>();

  matches.forEach((item) => {
    const normalized = item.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      unique.push(normalized);
    }
  });

  return unique;
}

export function normalizeEmailToken(token: string) {
  return token
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/^mailto:/i, "")
    .replace(/^[<({["']+/, "")
    .replace(/[>)}\]"',;:]+$/, "");
}

export function analyzeRecipients(rawValue: string) {
  const valid = extractEmails(rawValue);
  const validSet = new Set(valid.map((email) => email.toLowerCase()));
  const invalid: string[] = [];
  const seen = new Set<string>();

  rawValue
    .split(EMAIL_TOKEN_SPLIT_REGEX)
    .map((token) => normalizeEmailToken(token))
    .forEach((token) => {
      if (!token || !token.includes("@")) {
        return;
      }
      if (validSet.has(token.toLowerCase())) {
        return;
      }
      if (STRICT_EMAIL_REGEX.test(token)) {
        return;
      }

      const key = token.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        invalid.push(token);
      }
    });

  return { valid, invalid };
}

export function humanizeReleaseChannel(channel?: string) {
  if ((channel || "").toLowerCase() === "beta") {
    return "Bêta";
  }
  if ((channel || "").toLowerCase() === "alpha") {
    return "Alpha";
  }
  return channel || "Stable";
}

export function getEnabledCampaignFeatures(form: CampaignFormState) {
  const enabled: string[] = [];
  if (form.tracking_enabled) {
    enabled.push("ouvertures estimées");
  }
  if (form.unsubscribe_enabled) {
    enabled.push("lien de désinscription");
  }
  return enabled;
}

export function buildPublicUrlNotice(form: CampaignFormState, settingsForm: NewsletterSettings) {
  const enabledFeatures = getEnabledCampaignFeatures(form);
  const publicBaseUrl = (settingsForm.public_base_url || "").trim();

  if (!form.unsubscribe_enabled) {
    return {
      isWarning: true,
      message:
        "Attention: une newsletter doit normalement proposer une désinscription simple. Désactivez cette option seulement pour un cas vraiment exceptionnel.",
    };
  }

  if (enabledFeatures.length === 0) {
    return null;
  }

  const featureText =
    enabledFeatures.length === 2
      ? "Les ouvertures estimées et le lien de désinscription"
      : enabledFeatures[0] === "ouvertures estimées"
        ? "Les ouvertures estimées"
        : `Le ${enabledFeatures[0]}`;

  return {
    isWarning: !publicBaseUrl,
    message: !publicBaseUrl
      ? `${featureText} nécessitent une URL publique. Renseignez-la dans Paramètres > Expéditeur.`
      : `${featureText} utiliseront l'URL publique définie dans Paramètres > Expéditeur.`,
  };
}

export function editorHtmlHasContent(value: string) {
  const container = document.createElement("div");
  container.innerHTML = value || "";
  const textContent = (container.textContent || "").replace(/\u00a0/g, " ").trim();
  return textContent.length > 0 || Boolean(container.querySelector("img, hr, table, iframe"));
}

export function inferSettingsFieldErrors(message: string) {
  const content = (message || "").toLowerCase();

  if (content.includes("compte mail")) {
    return ["smtp_username"];
  }
  if (content.includes("adresse d'envoi")) {
    return ["sender_email"];
  }
  if (content.includes("reply-to")) {
    return ["reply_to_email"];
  }
  if (content.includes("url publique")) {
    return ["public_base_url"];
  }
  if (content.includes("port smtp")) {
    return ["smtp_port"];
  }
  if (content.includes("port imap")) {
    return ["imap_port"];
  }
  if (content.includes("taille de lot")) {
    return ["default_batch_size"];
  }
  if (content.includes("délai entre lots")) {
    return ["default_batch_delay_seconds"];
  }
  if (content.includes("smtp")) {
    return ["smtp_host", "smtp_port", "smtp_security", "smtp_username", "smtp_password"];
  }
  if (content.includes("imap")) {
    return ["imap_host", "imap_port", "imap_ssl", "imap_username", "imap_password", "imap_folder"];
  }

  return [];
}

export function inferCampaignFieldErrors(message: string) {
  const content = (message || "").toLowerCase();

  if (content.includes("nom de campagne")) {
    return ["campaign_name"];
  }
  if (content.includes("l'objet")) {
    return ["campaign_subject"];
  }
  if (content.includes("contenu du mail")) {
    return ["campaign_editor"];
  }
  if (
    content.includes("liste des adresses") ||
    content.includes("adresse email valide") ||
    content.includes("adresses email invalides")
  ) {
    return ["campaign_recipients"];
  }
  if (content.includes("date et l'heure d'envoi")) {
    return ["campaign_scheduled_at"];
  }

  return [];
}

export function inferTestEmailFieldErrors(message: string) {
  const content = (message || "").toLowerCase();

  if (content.includes("compte mail")) {
    return ["smtp_username"];
  }
  if (content.includes("adresse d'envoi")) {
    return ["sender_email"];
  }
  if (content.includes("l'objet")) {
    return ["campaign_subject"];
  }
  if (content.includes("contenu du mail")) {
    return ["campaign_editor"];
  }

  return [];
}

export function resolveFieldErrors(error: unknown, fallback: string[] = []) {
  const apiError = error as ApiError;
  if (Array.isArray(apiError?.fieldErrors) && apiError.fieldErrors.length > 0) {
    return apiError.fieldErrors;
  }
  return fallback;
}

export function readSettingsDraft() {
  try {
    const rawDraft = window.localStorage.getItem(SETTINGS_DRAFT_STORAGE_KEY);
    if (!rawDraft) {
      return null;
    }
    return normalizeSettingsForm(JSON.parse(rawDraft));
  } catch (error) {
    console.warn(error);
    return null;
  }
}

export function persistSettingsDraft(form: NewsletterSettings) {
  try {
    window.localStorage.setItem(SETTINGS_DRAFT_STORAGE_KEY, JSON.stringify(form));
  } catch (error) {
    console.warn(error);
  }
}

export function clearSettingsDraft() {
  try {
    window.localStorage.removeItem(SETTINGS_DRAFT_STORAGE_KEY);
  } catch (error) {
    console.warn(error);
  }
}

function isIncomingComposeDraft(value: unknown): value is NewsletterComposeDraft {
  if (!value || typeof value !== "object") {
    return false;
  }

  const draft = value as Partial<NewsletterComposeDraft>;
  return (
    draft.source === "event-planner" &&
    typeof draft.name === "string" &&
    typeof draft.subject === "string" &&
    typeof draft.html_body === "string" &&
    (draft.assistant_draft === undefined || isIncomingComposeAssistantDraft(draft.assistant_draft)) &&
    Array.isArray(draft.source_event_ids) &&
    typeof draft.period_start === "string" &&
    typeof draft.period_end === "string"
  );
}

function isIncomingComposeAssistantDraft(value: unknown): value is NewsletterComposeAssistantDraft {
  if (!value || typeof value !== "object") {
    return false;
  }

  const draft = value as Partial<NewsletterComposeAssistantDraft>;
  return (
    (draft.template_id === "digest" || draft.template_id === "story" || draft.template_id === "events") &&
    Boolean(draft.theme && typeof draft.theme === "object") &&
    Array.isArray(draft.sections) &&
    (draft.header_logo_url === undefined || typeof draft.header_logo_url === "string") &&
    (draft.header_logo_alt === undefined || typeof draft.header_logo_alt === "string") &&
    typeof draft.intro_kicker === "string" &&
    typeof draft.intro_title === "string" &&
    typeof draft.intro_body === "string" &&
    (draft.intro_image_url === undefined || typeof draft.intro_image_url === "string") &&
    (draft.intro_image_alt === undefined || typeof draft.intro_image_alt === "string") &&
    (draft.intro_image_layout === undefined ||
      draft.intro_image_layout === "left" ||
      draft.intro_image_layout === "right" ||
      draft.intro_image_layout === "stacked") &&
    (draft.intro_image_width_ratio === undefined ||
      draft.intro_image_width_ratio === null ||
      typeof draft.intro_image_width_ratio === "number") &&
    typeof draft.closing_note === "string"
  );
}

export function readIncomingComposeDraft() {
  try {
    const rawDraft = window.localStorage.getItem(NEWSLETTER_COMPOSE_STORAGE_KEY);
    if (!rawDraft) {
      return null;
    }

    const parsedDraft = JSON.parse(rawDraft);
    if (!isIncomingComposeDraft(parsedDraft)) {
      window.localStorage.removeItem(NEWSLETTER_COMPOSE_STORAGE_KEY);
      return null;
    }

    return parsedDraft;
  } catch (error) {
    console.warn(error);
    return null;
  }
}

export function clearIncomingComposeDraft() {
  try {
    window.localStorage.removeItem(NEWSLETTER_COMPOSE_STORAGE_KEY);
  } catch (error) {
    console.warn(error);
  }
}
